import { ActivitiesService } from '@/api';
import type { ActivityResponse, ActivityListParams } from '@/types/activities';

/**
 * Server page size used while paging the full filtered result for CSV export.
 * The backend clamps page_size to 100, so we page rather than request one huge
 * page.
 */
export const EXPORT_PAGE_SIZE = 100;

/**
 * Safety cap on the number of pages/rows the export loop will gather. Prevents
 * an unbounded number of requests for very large filter results. When the
 * server reports more than EXPORT_MAX_ROWS matching rows the export is
 * truncated to this many rows and `truncated` is set so the caller can warn the
 * user (see "no silent caps").
 */
export const EXPORT_MAX_PAGES = 50;
export const EXPORT_MAX_ROWS = EXPORT_PAGE_SIZE * EXPORT_MAX_PAGES; // 5000

export interface ActivitiesExportResult {
  /** All rows gathered (up to the safety cap). */
  rows: ActivityResponse[];
  /** The server-reported total matching the active filters. */
  total: number;
  /** True when `total` exceeded the safety cap and rows were truncated. */
  truncated: boolean;
}

/**
 * Page the activities list endpoint with the active filter set until every
 * matching row is gathered (or the safety cap is reached), then return the full
 * dataset for CSV export.
 *
 * Unlike the table's in-memory export, this honors the *server-side* filters
 * (date range, child, type, group, childcare) across ALL pages — not just the
 * rows loaded on the current page.
 *
 * - Loops `page_size=100`, incrementing `page` until collected rows reach
 *   `data.total` or the cap is hit.
 * - Forwards the filter params on every page.
 * - Never retries: any error (including 401/403) propagates to the caller, which
 *   surfaces it and stops paging.
 */
export async function fetchAllActivitiesForExport(
  params: ActivityListParams
): Promise<ActivitiesExportResult> {
  const rows: ActivityResponse[] = [];
  let total = 0;
  let truncated = false;

  const dateFrom = params.date_from?.toISOString();
  const dateTo = params.date_to?.toISOString();
  const type = params.type as Parameters<
    typeof ActivitiesService.getApiV1Activities
  >[3];

  for (let page = 1; page <= EXPORT_MAX_PAGES; page++) {
    // Errors (incl. 401/403) propagate to the caller — no retry here.
    const response = await ActivitiesService.getApiV1Activities(
      page,
      EXPORT_PAGE_SIZE,
      params.child_id,
      type,
      dateFrom,
      dateTo,
      params.group_id,
      params.childcare_id
    );

    total = response.total ?? 0;
    const items = response.items ?? [];
    rows.push(...items);

    // Stop when we've gathered everything or the server returned a short page.
    if (rows.length >= total || items.length === 0) {
      break;
    }

    // Safety cap: if the total exceeds what we're willing to page, stop and
    // signal truncation so the caller can warn the user.
    if (rows.length >= EXPORT_MAX_ROWS) {
      truncated = true;
      break;
    }
  }

  if (total > rows.length) {
    truncated = true;
  }

  return { rows, total, truncated };
}
